import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { acquireTokenForApi } from "@/authConfig";
import { FaRegEnvelope } from "react-icons/fa";
import "./NotificationsPanel.scss";

interface Notification {
  guid: string;
  messagesTitle: string;
  messagesIcon: string;
  message: string;
  status: string;
  createDt: string;
}

interface NotificationsPanelProps {
  open: boolean;
  messages: Notification[];
  onClose: () => void;
  onMessagesUpdate?: (updated: Notification[]) => void; // optional callback
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  open,
  messages,
  onClose,
  onMessagesUpdate,
}) => {
  const [localMessages, setLocalMessages] = useState<Notification[]>(messages);

  const updateMessages = (updated: Notification[]) => {
    setLocalMessages(updated);
    if (onMessagesUpdate) onMessagesUpdate(updated);
  };

  const clearMessage = async (guid: string) => {
    try {
      const token = await acquireTokenForApi();
      await fetch(`${import.meta.env.VITE_API_URL}/api/activitylog/clear/${guid}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      updateMessages(localMessages.filter((m) => m.guid !== guid));
    } catch (err) {
      console.error("Failed to clear message", err);
    }
  };

  const clearAll = async () => {
    try {
      const token = await acquireTokenForApi();
      await fetch(`${import.meta.env.VITE_API_URL}/api/activitylog/clearall`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      updateMessages([]);
    } catch (err) {
      console.error("Failed to clear all messages", err);
    }
  };

  return (
    <aside className={`message-center ${open ? "open" : ""}`}>
      <div className="d-flex justify-content-between align-items-center mb-2 px-2">
        <h4 className="message-center-title">
          <i className="far fa-envelope me-2"></i> Messages
        </h4>
        <div>
          <Button
            size="sm"
            variant="outline-secondary"
            className="me-2"
            onClick={clearAll}
          >
            Clear All
          </Button>
          <Button size="sm" variant="outline-danger" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {localMessages.length === 0 ? (
        <div className="text-center text-muted mt-4">
          <em>No messages this session.</em>
          <div>
            <FaRegEnvelope className="h1" />
          </div>
        </div>
      ) : (
        <div className="messages">
          {localMessages.map((msg) => (
            <div
              key={msg.guid}
              className={`single-message border p-2 mb-2 rounded ${
                msg.status === "Failed"
                  ? "message-failed border-danger"
                  : msg.status === "Success" || msg.status === "Completed"
                  ? "message-success border-success"
                  : msg.status === "New"
                  ? "border-primary"
                  : "border-secondary"
              }`}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <i className={`${msg.messagesIcon} me-2`} />
                  <strong className="message-title">{msg.messagesTitle}</strong>
                </div>
                <small className="message-createdt">
                  {new Date(msg.createDt).toLocaleTimeString()}
                </small>
              </div>
              <div
                className="message-description"
                dangerouslySetInnerHTML={{ __html: msg.message }}
              />
              <div className="text-end mt-1">
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger p-0"
                  onClick={() => clearMessage(msg.guid)}
                >
                  Clear
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </aside>
  );
};

export default NotificationsPanel;